import {
  Circle2d,
  Mat,
  OverlayUtil,
  SIDES,
  Vec
} from "@tldraw/editor";
class ShapeHandleOverlayUtil extends OverlayUtil {
  static type = "shape_handle";
  options = { zIndex: 200, lineWidth: 1.5 };
  isActive() {
    const editor = this.editor;
    if (editor.getIsReadonly() || editor.getInstanceState().isChangingStyle) return false;
    const onlySelectedShape = editor.getOnlySelectedShape();
    if (!onlySelectedShape) return false;
    const handles = editor.getShapeHandles(onlySelectedShape);
    if (!handles) return false;
    if (editor.isInAny("select.idle", "select.pointing_handle")) {
      return true;
    }
    if (editor.isIn("select.editing_shape")) {
      return editor.isShapeOfType(onlySelectedShape, "note");
    }
    return false;
  }
  getOverlays() {
    const editor = this.editor;
    const onlySelectedShape = editor.getOnlySelectedShape();
    if (!onlySelectedShape) return [];
    const handles = editor.getShapeHandles(onlySelectedShape);
    if (!handles) return [];
    if (editor.isShapeHidden(onlySelectedShape)) return [];
    const zoom = editor.getZoomLevel();
    const isCoarse = editor.getInstanceState().isCoarsePointer;
    const minDist = (isCoarse ? editor.options.coarseHandleRadius : editor.options.handleRadius) / zoom * 2;
    const vertexHandles = handles.filter((handle) => handle.type === "vertex");
    const vertexHandlesForHitTest = [];
    const otherHandlesForHitTest = [];
    for (const handle of handles) {
      if (handle.type === "virtual" && vertexHandles.some((vertexHandle) => Vec.Dist(handle, vertexHandle) < minDist)) {
        continue;
      }
      if (handle.type === "vertex") {
        vertexHandlesForHitTest.push(handle);
      } else {
        otherHandlesForHitTest.push(handle);
      }
    }
    return vertexHandlesForHitTest.concat(otherHandlesForHitTest).map((handle) => ({
      id: `handle:${onlySelectedShape.id}:${handle.id}`,
      type: "shape_handle",
      props: {
        shapeId: onlySelectedShape.id,
        handle
      }
    }));
  }
  getGeometry(overlay) {
    const editor = this.editor;
    const { shapeId, handle } = overlay.props;
    const transform = editor.getShapePageTransform(shapeId);
    if (!transform) return null;
    const zoom = editor.getZoomLevel();
    const isCoarse = editor.getInstanceState().isCoarsePointer;
    const radius = (isCoarse ? editor.options.coarseHandleRadius : editor.options.handleRadius) / zoom;
    const pagePoint = Mat.applyToPoint(transform, { x: handle.x, y: handle.y });
    return new Circle2d({
      x: pagePoint.x - radius,
      y: pagePoint.y - radius,
      radius,
      isFilled: true
    });
  }
  getCursor(_overlay) {
    return "grab";
  }
  render(ctx, overlays) {
    if (overlays.length === 0) return;
    const editor = this.editor;
    const shapeId = overlays[0].props.shapeId;
    const transform = editor.getShapePageTransform(shapeId);
    if (!transform) return;
    const zoom = editor.getZoomLevel();
    const isCoarse = editor.getInstanceState().isCoarsePointer;
    const themeColors = editor.getCurrentTheme().colors[editor.getColorMode()];
    const fgColor = themeColors.selectedContrast;
    const strokeColor = themeColors.selectionStroke;
    const bgFill = themeColors.selectionFill;
    const hoveredOverlayId = editor.overlays.getHoveredOverlayId();
    const bgRadius = (isCoarse ? editor.options.coarseHandleRadius : editor.options.handleRadius) / zoom;
    ctx.save();
    ctx.transform(transform.a, transform.b, transform.c, transform.d, transform.e, transform.f);
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = this.options.lineWidth / zoom;
    for (let i = overlays.length - 1; i >= 0; i--) {
      const overlay = overlays[i];
      const { handle } = overlay.props;
      const isHovered = overlay.id === hoveredOverlayId;
      if (handle.type === "create" && !isCoarse && !isHovered) continue;
      if (isHovered) {
        ctx.fillStyle = bgFill;
        ctx.beginPath();
        ctx.arc(handle.x, handle.y, bgRadius, 0, Math.PI * 2);
        ctx.fill();
      }
      if (handle.type === "clone") {
        const fr2 = 3 / zoom;
        const sideIndex = SIDES.indexOf(handle.id);
        const rotation = -Math.PI / 2 * (1 - sideIndex);
        ctx.save();
        ctx.translate(handle.x, handle.y);
        ctx.rotate(rotation);
        ctx.fillStyle = strokeColor;
        ctx.beginPath();
        ctx.moveTo(0, -fr2);
        ctx.arc(0, 0, fr2, -Math.PI / 2, Math.PI / 2);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        continue;
      }
      const fr = (handle.type === "create" && isCoarse ? 3 : 4) / Math.max(zoom, 0.25);
      ctx.fillStyle = fgColor;
      ctx.beginPath();
      ctx.arc(handle.x, handle.y, fr, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
  }
}
export {
  ShapeHandleOverlayUtil
};
//# sourceMappingURL=ShapeHandleOverlayUtil.mjs.map
